/* eslint-disable no-shadow */
/* eslint-disable prefer-destructuring */
/* eslint-disable no-use-before-define */
import PropTypes from 'prop-types';
import * as queryString from 'query-string';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';

import PopOverMenu from '../../../../../components/PopOverMenu';
import EventPostScorePopup from '../../../../../components/PopUps/EventPostScorePopup';
import TabBar from '../../../../../components/TabBar';
import AllStandingsV3 from './AllStandings';
import RoundV2 from './RoundV2';
import StandingsV3 from './StandingsV3';

import * as eventProfileActions from '../../../../../actions/event-profile-actions';
import { useStatsig } from '../../../../../components/hooks/useStatsig';
import { drawCategoryTypeIds, sportTypeIds } from '../../../../../utils/eventConstants';
import { normalizeMatchPositionsForPostScore } from '../../../../../utils/postScore';
import { allStandingsEnabled, isEventPTT, isEventType, isOrganizer, EventTypes } from '../../../../../utils/events';

import { getDrawName, getParentDrawName } from '../../../../../utils/courtSchedular';
import { createTypeByIdDict } from '../../../../../utils/formatting';
import { resolveDisplayedDraws, buildChildDrawMenuItems, resolveUserDrawSelection, buildDrawTabs } from '../../../../../utils/drawDisplayUtils';

import styles from '../../../event.scss';
import ProDrawAllStandingV3 from './ProDrawAllStandingV3';
import { routerLocationShape, routerHistoryShape } from '../../../../../propTypes';

const DrawsTabContentV3 = (props) => {
    const { sportTypeId } = props;
    const auth = useSelector(({ auth }) => auth);
    const coreUI = useSelector(({ coreUI }) => coreUI);
    const { event, currentMemberRegistration, draws, drawsTabState, currentDraw, parentDraw, stats, drawAllStats } = useSelector(({ eventProfile }) => {
        const { event, currentMemberRegistration, draws, drawsTabState, currentDraw, parentDraw, stats, drawAllStats } = eventProfile;
        return { event, currentMemberRegistration, draws, drawsTabState, currentDraw, parentDraw, stats, drawAllStats };
    });
    const dispatch = useDispatch();
    const tabBarRef = useRef(null);
    const isPttEvent = isEventPTT(event?.data);
    const isProDraw = parentDraw?.data?.categoryTypeId === drawCategoryTypeIds.PRO_DRAW;

    const { drawsDict } = draws;
    const { activeRound, showStandings } = drawsTabState || {};
    const parsed = queryString.parse(props.location.search);

    const { types: { tms: { draw: { drawCategoryTypes, drawTagTypes }, division: { tournament: { drawEliminationTypes } } } } } = coreUI;
    const drawCategoryTypesDict = createTypeByIdDict(drawCategoryTypes);

    const [tabs, setTabs] = useState([]);
    const [userDrawIds, setUserDrawIds] = useState([]);
    const [standingsEnabled, setStandingsEnabled] = useState();
    const [postScore, setPostScore] = useState({ showModal: false });

    const isFlexLeagueEvent = isEventType(event?.data, EventTypes.FLEX_LEAGUE);
    const isCurrentMemberAdmin = !!(auth?.user && event?.data && isOrganizer(event.data));
    const isUserInCurrentDraw = (userDrawIds?.length > 0) && userDrawIds.includes(currentDraw?.data?.id);
    const showFlexLeagueContactFooter = isFlexLeagueEvent && (isCurrentMemberAdmin || isUserInCurrentDraw);
    const showPostScoreForParticipant = isFlexLeagueEvent && isUserInCurrentDraw;

    const isUserParticipatingInMatch = useCallback((match) => {
        const userId = auth?.user?.id;
        if (!userId) {
            return false;
        }
        const participatingMembers = match?.participatingMembers ?? [];
        if (participatingMembers.some(m => m.id === userId)) {
            return true;
        }
        const memberIds = [...(match?.position1?.memberIds || []), ...(match?.position2?.memberIds || [])];
        return memberIds.includes(userId);
    }, [auth?.user?.id]);

    const { checkGate, featureGates } = useStatsig();
    const isAllStandingsFlagEnabled = checkGate(featureGates.ALL_STANDINGS);

    const firstPublishedDraw = (draws.data?.find(draw => draw.published)) || null;

    const { displayedParentDraw, displayedCurrentDraw } = resolveDisplayedDraws({
        parentDraw,
        currentDraw,
        drawsDict,
        firstPublishedDraw,
    });

    const isCutOffRound = (roundIndex) => {
        return currentDraw.data?.feedOutRound && roundIndex + 1 > currentDraw.data.feedOutRound;
    };

    const showNextRound = !!currentDraw.data?.rounds?.[activeRound + 1]?.matches && !isCutOffRound(activeRound + 1);
    const showRoundAfterNext = !!currentDraw.data?.rounds?.[activeRound + 2]?.matches && !isCutOffRound(activeRound + 2);

    const drawStandingsLoading = currentDraw.loading || (currentDraw.data && stats.loading);
    const drawStandings = currentDraw.data && stats.data && stats.drawId === currentDraw.data.id ? stats.data : null;
    const allStandingsLoading = parentDraw.loading || (parentDraw.data && drawAllStats.loading);
    const allStandings = parentDraw.data && drawAllStats.data;

    useEffect(() => {
        findUserDraw();
    }, [currentMemberRegistration.data]);

    useEffect(() => {
        populateTabs();
    }, [currentDraw.data, drawAllStats.data, activeRound, showStandings]);

    useEffect(() => {
        if (parentDraw.data?.id) {
            const isAllStandingsEnabled = parentDraw.data.eliminationTypeId && allStandingsEnabled(parentDraw.data) && isAllStandingsFlagEnabled;
            if (isAllStandingsEnabled) {
                dispatch(eventProfileActions.getDrawAllStats(event.data.id, parentDraw.data?.id));
            }
        }
    }, [parentDraw.data?.id]);

    const setTabBarRef = (node) => {
        tabBarRef.current = node;
    };

    const findUserDraw = () => {
        if (!auth.user?.id) {
            return;
        }
        const userDrawIdsUpdate = (currentMemberRegistration.data?.draws ?? [])
            .flatMap(draw => [draw.id, draw.parentDrawId].filter(Boolean));
        if (userDrawIdsUpdate.length === 0) {
            return;
        }
        setUserDrawIds(userDrawIdsUpdate);

        const selection = resolveUserDrawSelection(draws.data, userDrawIdsUpdate);
        if (selection) {
            getDraw(selection.drawToSelect, selection.dontAutoLoadFirstChildDraw);
        }
    };

    const populateTabs = () => {
        const { tabs: tabsUpdate, standingsEnabled } = buildDrawTabs({ currentDraw, showStandings, activeRound, drawEliminationTypes });
        setTabs(tabsUpdate);
        setStandingsEnabled(standingsEnabled);
    };

    const getDraw = (drawId, dontAutoLoadFirstChildDraw = false) => {
        draws.data.forEach(parentDraw => {
            if (parentDraw.id === drawId) {
                dispatch(eventProfileActions.getDraw({ eventId: event.data.id, drawId: parentDraw.id, setAsCurrentDraw: true }));
            } else {
                parentDraw.childDraws.forEach(childDraw => {
                    if (childDraw.id === drawId) {
                        dispatch(eventProfileActions.getDraw({ eventId: event.data.id, drawId: parentDraw.id, dontAutoLoadFirstChildDraw }));
                        dispatch(eventProfileActions.getDraw({ eventId: event.data.id, drawId: childDraw.id, isChildDraw: true, dontAutoLoadFirstChildDraw }));
                    }
                });
            }
        });
    };

    const handleParentDrawSelect = (selectedParentDraw) => {
        const draw = draws.data.find(parentDraw => {
            return parentDraw.id === selectedParentDraw.value;
        });
        const playingChildDraw = draw.childDraws.find(childDraw => {
            return userDrawIds.includes(childDraw.id);
        });
        const drawToSelect = playingChildDraw ? playingChildDraw.id : draw.id;
        const dontAutoLoadFirstChildDraw = !!playingChildDraw;

        getDraw(drawToSelect, dontAutoLoadFirstChildDraw);
        updateDrawsTabState({
            ...drawsTabState,
            allStandings: false,
            activeRound: 0,
        }, drawToSelect);
    };

    const handleChildDrawSelect = (selectedDraw) => {
        if (!selectedDraw.value) {
            updateDrawsTabState({
                ...drawsTabState,
                allStandings: true,
            });
        } else {
            dispatch(eventProfileActions.getDraw({ eventId: event.data.id, drawId: selectedDraw.value, isChildDraw: true }));
            updateDrawsTabState({
                ...drawsTabState,
                allStandings: false,
                activeRound: 0,
            }, selectedDraw.value);
        }
    };

    const handleDrawsTabClick = (ev, i) => {
        const drawsTabStateUpdate = { ...drawsTabState };
        if (standingsEnabled) {
            drawsTabStateUpdate.activeRound = i === 0 ? 0 : i - 1;
            drawsTabStateUpdate.showStandings = i === 0;
        } else {
            drawsTabStateUpdate.activeRound = i;
        }
        updateDrawsTabState(drawsTabStateUpdate);
        const tabsUpdate = tabs.map((tab, j) => (i === j ? { ...tab, active: true } : { ...tab, active: false }));
        setTabs(tabsUpdate);
    };

    const printDraw = (drawId) => {
        if (drawId) {
            if (window) {
                window.open(`/events/${event.data.id}/draws/${drawId}/print`, '_blank');
            } else {
                props.history.push(`/events/${event.data.id}/draws/${drawId}/print`);
            }
        } else {
            // No drawId provided — nothing to do
        }
    };

    const updateDrawsTabState = (drawsTabState, overrideDrawId = null) => {
        parsed.d = overrideDrawId != null ? overrideDrawId : currentDraw.data?.id;
        parsed.r = drawsTabState.activeRound || 0;
        parsed.standings = drawsTabState.showStandings ? '1' : undefined;
        props.history.replace({
            search: queryString.stringify(parsed),
        });
        dispatch(eventProfileActions.setDrawsTabState(drawsTabState));
    };

    const childDrawMenuItems = buildChildDrawMenuItems({
        displayedParentDraw,
        drawsDict,
        drawCategoryTypesDict,
        isAllStandingsFlagEnabled,
        userDrawIds,
    });

    const handlePostScore = (roundIndex, matchIndex) => {
        setPostScore({
            showModal: true,
            roundIndex,
            matchIndex,
        });
    };

    const handleClearScore = async (matchId) => {
        try {
            await dispatch(eventProfileActions.clearScore({ id: matchId }, currentDraw?.data?.id));
        } catch (err) {
            console.log(err);
        }
    };

    const getChildDrawName = () => {
        if (drawsTabState.allStandings) {
            return 'All standings';
        }

        if (!displayedCurrentDraw || !displayedParentDraw) {
            return '';
        }

        return getDrawName(displayedCurrentDraw, displayedParentDraw, drawCategoryTypesDict);
    };

    if (draws.error) {
        return (
            <div className="row">
                <div className="col-md-8">
                    <h6 className="" data-testid="draws-tab.error.title">Draws</h6>
                    <p data-testid="draws-tab.error.msg">Error Loading Draws...</p>
                </div>
            </div>
        );
    }

    if (draws.loading || draws.data === null) {
        return (
            <div className="row">
                <div className="col-md-8">
                    <h6 className="" data-testid="draws-tab.loading.title">Draws</h6>
                    <p data-testid="draws-tab.loading.msg">Loading Draws...</p>
                </div>
            </div>
        );
    }

    let drawBracketContent;
    if (currentDraw.loading) {
        drawBracketContent = (
            <div className="row">
                <div className="col">
                    <p data-testid="draws-tab.bracket.loading-msg">Loading matches...</p>
                </div>
            </div>
        );
    } else if (currentDraw.error) {
        drawBracketContent = (
            <div className="row">
                <div className="col">
                    <p className="text-danger" data-testid="draws-tab.bracket.error-msg">Error loading draw data...</p>
                </div>
            </div>
        );
    } else {
        drawBracketContent = (
            <div className="row">
                <RoundV2
                    currentDraw={currentDraw}
                    round={activeRound}
                    eventSchedule={event.data.eventSchedule}
                    columnNumber={1}
                    displayOnMobile={true}
                    handlePostScore={handlePostScore}
                    handleClearScore={handleClearScore}
                    showContactFooter={showFlexLeagueContactFooter}
                    showPostScoreForParticipant={showPostScoreForParticipant}
                    isUserParticipatingInMatch={isUserParticipatingInMatch}
                />
                {showNextRound && (
                    <RoundV2
                        currentDraw={currentDraw}
                        round={activeRound + 1}
                        eventSchedule={event.data.eventSchedule}
                        columnNumber={2}
                        handlePostScore={handlePostScore}
                        handleClearScore={handleClearScore}
                        showContactFooter={showFlexLeagueContactFooter}
                        showPostScoreForParticipant={showPostScoreForParticipant}
                        isUserParticipatingInMatch={isUserParticipatingInMatch}
                    />
                )}
                {showRoundAfterNext && (
                    <RoundV2
                        currentDraw={currentDraw}
                        round={activeRound + 2}
                        eventSchedule={event.data.eventSchedule}
                        columnNumber={3}
                        handlePostScore={handlePostScore}
                        handleClearScore={handleClearScore}
                        showContactFooter={showFlexLeagueContactFooter}
                        showPostScoreForParticipant={showPostScoreForParticipant}
                        isUserParticipatingInMatch={isUserParticipatingInMatch}
                    />
                )}
            </div>
        );
    }

    return (
        <>
            {firstPublishedDraw && (
                <div className="row">
                    <div className="col">
                        <div className={`d-flex mt24 mb12 noselect aic ${styles.filters}`}>
                            {(draws.data?.length === 1) && (
                                <div className={styles.anchorClass}>{firstPublishedDraw.name}</div>
                            )}
                            {(draws.data?.length > 1) && (
                                <div className={`${styles.filter} mw0`}>
                                    <PopOverMenu
                                        menuItems={draws.data?.filter(draw => draw.published).map(draw => {
                                            return draw.published ? {
                                                value: draw.id,
                                                label: getParentDrawName(draw, draw),
                                                playing: userDrawIds.includes(draw.id) || draw.childDraws?.some(cd => userDrawIds.includes(cd.id)),
                                            } : null;
                                        })}
                                        onSelected={handleParentDrawSelect}
                                        alignLeft={true}
                                        selectedValue={displayedParentDraw?.id}
                                        data-testid="event-profile.draws.division-dropdown"
                                    >
                                        <div className={`${styles.anchorClass}`}>
                                            <div className={`ellipsis`}>
                                                {getParentDrawName(displayedParentDraw, displayedParentDraw)}
                                            </div>
                                            <i className={`${styles.arrow} uts uts-expand fs18`} />
                                        </div>
                                    </PopOverMenu>
                                </div>
                            )}
                            {childDrawMenuItems.length > 1 && (
                                <div className={styles.filter}>
                                    <PopOverMenu
                                        anchorClass={styles.anchorClass}
                                        popOverMenuClass="flex-right"
                                        menuItems={childDrawMenuItems}
                                        onSelected={handleChildDrawSelect}
                                        alignLeft={true}
                                        selectedValue={drawsTabState.allStandings ? '' : displayedCurrentDraw?.id}
                                        data-testid="event-profile.draws.standings-dropdown"
                                    >
                                        <span className="pl8">{getChildDrawName()}</span> <i className={`${styles.arrow} uts uts-expand`} />
                                    </PopOverMenu>
                                </div>
                            )}
                            <div className="d-none d-md-block ml-auto">
                                <div className="text-right">
                                    {parentDraw.data && auth?.user && (
                                        <button
                                            type="button"
                                            className="btn btn-small btn-default float-right"
                                            onClick={() => printDraw(parentDraw.data.id)}
                                            data-testid="draws-tab.filters.print-btn"
                                        >
                                            <i className="fas fa-print" /> Print
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {!firstPublishedDraw && (
                <div className="row">
                    <div className="col-md-8">
                        <h6 className="" data-testid="draws-tab.unpublished.title">Draws</h6>
                        <p data-testid="draws-tab.unpublished.msg">
                            Draws haven’t been published<br />
                            Check back to view draws once published.
                        </p>
                    </div>
                </div>
            )}
            {drawsTabState?.allStandings && (
                <>
                    {parentDraw.error && (
                        <div className="row">
                            <div className="col">
                                <p className="text-danger" data-testid="draws-tab.all-standings.parent-draw-error-msg">Error loading parent draw data...</p>
                            </div>
                        </div>
                    )}
                    {!parentDraw.error && (
                        <>
                            {isProDraw && (
                                <ProDrawAllStandingV3
                                    draw={parentDraw?.data}
                                    drawsList={draws.data}
                                    sportTypeId={sportTypeId}
                                    auth={auth}
                                    isPttEvent={isPttEvent}
                                    event={event?.data}
                                    drawTagTypes={drawTagTypes}
                                />
                            )}
                            {!isProDraw && (
                                <>
                                    <TabBar
                                        tabs={[{
                                            title: 'Standings',
                                            showTab: true,
                                            active: true,
                                        }]}
                                        handleTabClick={() => { }}
                                        ref={setTabBarRef}
                                        eventName="Draws Tab Clicked"
                                        transparent={true}
                                        noContainer={true}
                                        clickCategory="Event Profile"
                                        clickLocation="Draws Tab"
                                        data-testid="event-profile.draws.standings-tab-bar"
                                    />
                                    <div className={`container bg-transparent matches`}>
                                        <div className="row">
                                            <div className="col">
                                                <AllStandingsV3
                                                    allStandingsLoading={allStandingsLoading}
                                                    allStandings={allStandings}
                                                    sportTypeId={sportTypeId}
                                                    auth={auth}
                                                    isPttEvent={isPttEvent}
                                                    draw={parentDraw.data}
                                                    event={event.data}
                                                    drawTagTypes={drawTagTypes}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </>
            )}
            {!drawsTabState?.allStandings && firstPublishedDraw && drawsTabState && (currentDraw.loading || !!currentDraw.data?.rounds.length) && (
                <div className={`${styles.section} bg-transparent main-content`}>
                    {!currentDraw.loading && (
                        <TabBar
                            tabs={tabs}
                            handleTabClick={handleDrawsTabClick}
                            ref={setTabBarRef}
                            eventName="Draws Tab Clicked"
                            transparent={true}
                            noContainer={true}
                            clickCategory="Event Profile"
                            clickLocation="Draws Tab"
                            data-testid="event-profile.draws.tab-bar"
                        />
                    )}
                    {!(showStandings && standingsEnabled) && (
                        <div className="container bg-transparent matches draws-bracket">
                            {drawBracketContent}
                        </div>
                    )}
                    <br />
                    {(showStandings && standingsEnabled) && (
                        <div className={`container bg-transparent matches`}>
                            {currentDraw.error ? (
                                <div className="row">
                                    <div className="col">
                                        <p className="text-danger" data-testid="draws-tab.standings.error-msg">Error loading standings data...</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="row">
                                    <div className="col">
                                        <StandingsV3
                                            drawStandingsLoading={drawStandingsLoading}
                                            drawStandings={drawStandings}
                                            sportTypeId={sportTypeId}
                                            auth={auth}
                                            isPttEvent={isPttEvent}
                                            drawTagTypes={drawTagTypes}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
            {currentDraw.data && postScore.showModal && (() => {
                const roundMatches = currentDraw.data.rounds?.[postScore.roundIndex]?.matches ?? [];
                const matchesForPostScore = roundMatches.map(normalizeMatchPositionsForPostScore);

                return (
                    <EventPostScorePopup
                        event={event.data}
                        drawsDict={drawsDict}
                        draw={currentDraw.data}
                        selectedDrawId={currentDraw.data.id}
                        roundIndex={postScore.roundIndex}
                        matchIndex={postScore.matchIndex}
                        activeRound={activeRound}
                        matches={matchesForPostScore}
                        onCloseClick={() => setPostScore({ showModal: false })}
                        postScoreAction={eventProfileActions.postScore}
                    />
                );
            })()}
        </>
    );
};

DrawsTabContentV3.propTypes = {
    sportTypeId: PropTypes.number,
    location: routerLocationShape.isRequired,
    history: routerHistoryShape.isRequired,
};

DrawsTabContentV3.defaultProps = {
    sportTypeId: sportTypeIds.TENNIS,
};

export default withRouter(DrawsTabContentV3);
